var _excluded = ["alt", "block", "className", "containerClassName", "fileFormat", "lazy", "maxWidth", "mobileSrc", "prefix", "preload", "sizes", "src", "tag"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/* eslint-disable sonarjs/no-nested-template-literals,react/no-unknown-property */
import React, { useState, useEffect, useContext } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import cs from 'classnames';
// eslint-disable-next-line import/no-cycle
import { GridContainerContext } from '../grid-container';
require("./image.css");
var styles = {
  "blockImageContainer": "blockImageContainer-3NdK-",
  "lazyImageContainer": "lazyImageContainer-2KFMN",
  "picture": "picture-SAMy-",
  "pictureContainer": "pictureContainer-1dvrb",
  "blockImg": "blockImg-2_uGG"
}; // Returns a sizes attribute for an <img />
// breakpoints is an array of objects with a breakpoint property and a slotSize property
// ex:
// [{ breakpoint: "1372px", slotSize: "312px" },
// { breakpoint: "981px", slotSize: "23vw" },
// { breakpoint: "641px", slotSize: "31vw" },
// { slotSize: "46vw" }]
// Note that these are in descending order of (min-width) and order does matter
// The final member of breakpoints should be the default slot size for the img
// Disabling this es-lint rule because alt text is being passed in by
// spreading in props but es-lint isn't recognizing it (line 192)
/* eslint-disable jsx-a11y/alt-text */
export var constructSizes = breakpoints => {
  var defaultSlotSize = breakpoints.find(bp => !bp.breakpoint) && breakpoints.find(bp => !bp.breakpoint).slotSize;
  var validatedBreakpointsWithoutDefault = breakpoints.filter(bp => !!bp.breakpoint && bp.breakpoint.includes('px'));
  return validatedBreakpointsWithoutDefault.sort((a, b) => b.breakpoint.split('px')[0] - a.breakpoint.split('px')[0]).reduce((acc, _ref) => {
    var {
      breakpoint,
      slotSize
    } = _ref;
    return [...acc, "(min-width: ".concat(breakpoint, ") ").concat(slotSize)];
  }, []).concat(defaultSlotSize || []).join(', ');
};
var prefixRegex = /(\?\$(.*?)\$)$/im;

/**
 * @param {string} baseImageUrl
 * @return {boolean}
 */
export var hasExistingPrefix = baseImageUrl => {
  if (!baseImageUrl) return false;
  return prefixRegex.test(baseImageUrl);
};
export var DEFAULT_SRC_WIDTH = 1280;
export var FORMAT = Object.freeze({
  JPEG: 'jpeg',
  WEBP: 'webp'
});

/**
 * @param {string} baseImageUrl
 * @param {number} width
 * @return {string}
 */
export var getDefaultSrcPresets = _ref2 => {
  var {
    baseImageUrl,
    width,
    prefix
  } = _ref2;
  return hasExistingPrefix(baseImageUrl) ? baseImageUrl : "".concat(baseImageUrl, "?").concat(prefix ? "$".concat(prefix, "$&") : '', "wid=").concat(width || DEFAULT_SRC_WIDTH, "&op_usm=0.5,2,10,0&fmt=").concat(FORMAT.JPEG, "&qlt=75,1&fit=constrain,0&op_sharpen=0&resMode=sharp2&iccEmbed=0&printRes=72");
};
export var needsLegacyImgSupport = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false;
  return navigator.userAgent.match(/Trident/); // IE-only
};

export var scene7APISrcSet = _ref3 => {
  var {
    baseImageUrl,
    prefix,
    maxWidth,
    format
  } = _ref3;
  // common query params for all sizes
  // see https://lululemon.atlassian.net/wiki/spaces/CET/pages/827458212/Scene7+Image+Presets+for+PDP+webpages
  var baseScene7QueryParams = "fmt=".concat(format || FORMAT.JPEG, "&qlt=").concat(format === FORMAT.WEBP ? '80' : '75', ",1&fit=constrain,0&op_sharpen=0&resMode=sharp2&iccEmbed=0&printRes=72");

  // Image sharpening config for Scene7's unsharp mask (op_usm query parameter)
  // Comma-separated list made of values for amount, radius, threshold, and monochrome
  // Actual values come from https://lululemon.atlassian.net/wiki/spaces/CET/pages/858522766/USM+Setting+Optimization+for+PDP+Images
  //
  // best practices: https://marketing.adobe.com/resources/help/en_US/s7/sharpening/s7_sharpening_images.pdf
  // docs: https://docs.adobe.com/content/help/en/dynamic-media-classic/using/master-files/best-practices-optimizing-quality-images.html
  var unsharpMask1x = '0.8,1,10,0';
  var unsharpMask2x = '0.5,2,10,0';

  // determined after some research. some sources:
  // https://gs.statcounter.com/screen-resolution-stats/all/united-states-of-america
  // https://deviceatlas.com/blog/most-used-smartphone-screen-resolutions#us
  // https://developer.apple.com/library/archive/documentation/DeviceInformation/Reference/iOSDeviceCompatibility/Displays/Displays.html
  // https://medium.com/hceverything/applying-srcset-choosing-the-right-sizes-for-responsive-images-at-different-breakpoints-a0433450a4a3
  // akamai
  // this list can be edited or extended safely
  var scene7ImageWidths = [{
    width: 320,
    usm: unsharpMask1x
  },
  // small image on desktops or slow connections
  {
    width: 750,
    usm: unsharpMask1x
  },
  // phones 2x (iPhone 6,7,8)
  {
    width: 1080,
    usm: unsharpMask2x
  },
  // phones 2.6x (iPhone 6,7,8 Plus)
  {
    width: 1125,
    usm: unsharpMask2x
  },
  // phones 3x (iPhone X)
  {
    width: 1280,
    usm: unsharpMask2x
  },
  // desktops 1x, iPhone 3x, phones 3x
  {
    width: 1440,
    usm: unsharpMask2x
  },
  // desktops 1x, phones 3x
  {
    width: 1600,
    usm: unsharpMask2x
  },
  // desktops 1x, iPad 2x, phones 4x
  {
    width: 2420,
    usm: unsharpMask2x
  },
  // desktops 2x
  {
    width: 2644,
    usm: unsharpMask2x
  } // 2x grid max
  ];

  var constructSrcSetString = imageWidths => {
    return imageWidths.map(_ref4 => {
      var {
        width,
        usm
      } = _ref4;
      return "".concat(baseImageUrl, "?").concat(prefix ? "$".concat(prefix, "$&") : '', "wid=").concat(width, "&op_usm=").concat(usm, "&").concat(baseScene7QueryParams, " ").concat(width, "w");
    }).join(',\n');
  };

  // Optional maxWidth parameter.
  // Filters out imageWidths larger than this param and includes it in the list of image widths
  if (maxWidth) {
    var scene7ImageWidthsWithMaxWidth = [...scene7ImageWidths.filter(_ref5 => {
      var {
        width
      } = _ref5;
      return width < maxWidth;
    }), {
      width: maxWidth,
      usm: unsharpMask1x
    }];
    return constructSrcSetString(scene7ImageWidthsWithMaxWidth);
  }

  // most important detail: the `wid` image url param and the srcset `w` width are exactly the same
  return constructSrcSetString(scene7ImageWidths);
};
var Image = /*#__PURE__*/React.forwardRef((_ref6, ref
// eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  var {
      alt,
      block,
      className,
      containerClassName,
      fileFormat,
      lazy,
      maxWidth: maxWidthProp,
      mobileSrc,
      prefix,
      preload,
      sizes: sizesProp,
      src,
      tag: Tag
    } = _ref6,
    rest = _objectWithoutProperties(_ref6, _excluded);
  var [lazyLoadRef, inView] = useInView({
    triggerOnce: true,
    rootMargin: '300px'
  });
  var [imgFallback, setImgFallback] = useState('data:,');
  var imageSizesForGridLayout = useContext(GridContainerContext);
  var sizes = sizesProp || (imageSizesForGridLayout === null || imageSizesForGridLayout === void 0 ? void 0 : imageSizesForGridLayout.sizes);
  var maxWidth = maxWidthProp || (imageSizesForGridLayout === null || imageSizesForGridLayout === void 0 ? void 0 : imageSizesForGridLayout.maxWidth);
  useEffect(() => {
    if (needsLegacyImgSupport() || hasExistingPrefix(src)) {
      setImgFallback(getDefaultSrcPresets({
        baseImageUrl: src,
        width: maxWidth < DEFAULT_SRC_WIDTH ? maxWidth : DEFAULT_SRC_WIDTH
      }));
    }
  }, [src, maxWidth]);
  var constructSrcSet = (format, src, mobileSrc) => {
    var urlSrc = mobileSrc || src;
    return hasExistingPrefix(src) ? undefined : scene7APISrcSet({
      baseImageUrl: urlSrc,
      format: format,
      maxWidth,
      prefix
    });
  };
  var imageProps = function () {
    var loading = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    return _objectSpread(_objectSpread({}, rest), {}, {
      sizes,
      srcSet: constructSrcSet(fileFormat, src),
      loading,
      src: imgFallback,
      alt
    });
  };
  var containerClasses = cs(styles.lazyImageContainer, containerClassName, {
    // Chrome has a bug where intersection observer isn't working consistently
    // with inline elements, this forces the span to be block level if out of view
    [styles.blockImageContainer]: !inView && Tag === 'span'
  });
  var fileFormats = fileFormat ? [fileFormat] : [FORMAT.WEBP, FORMAT.JPEG];
  var sources = fileFormats.map((format, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: format
  }, /*#__PURE__*/React.createElement("source", {
    sizes: sizes,
    media: mobileSrc && '(min-width: 500px)',
    srcSet: constructSrcSet(format, src),
    type: "image/".concat(format),
    "data-testid": "source-".concat(index)
  }), mobileSrc && /*#__PURE__*/React.createElement("source", {
    sizes: sizes,
    media: !sizes ? '(min-width: 0px)' : null,
    srcSet: constructSrcSet(format, src, mobileSrc),
    type: "image/".concat(format),
    "data-testid": "source-".concat(index, "-sm")
  })));
  var preloadImgLink = /*#__PURE__*/React.createElement("link", {
    as: "image",
    "data-testid": "preload-img-link",
    imagesizes: sizes,
    imagesrcset: constructSrcSet('webp', src),
    rel: "preload",
    type: "image/webp"
  });
  return lazy ? /*#__PURE__*/React.createElement(Tag, {
    className: containerClasses,
    ref: lazyLoadRef,
    "data-testid": "image"
  }, preload && /*#__PURE__*/React.createElement(Head, null, preloadImgLink), inView && /*#__PURE__*/React.createElement("picture", {
    className: styles.picture
  }, sources, /*#__PURE__*/React.createElement("img", _extends({}, imageProps('lazy'), {
    className: cs({
      [styles.blockImg]: !!block,
      [className]: !!className
    }),
    "data-testid": "lazy-image",
    ref: ref
  })))) : /*#__PURE__*/React.createElement(React.Fragment, null, preload && /*#__PURE__*/React.createElement(Head, null, preloadImgLink), /*#__PURE__*/React.createElement("picture", {
    className: cs(styles.pictureContainer)
  }, sources, /*#__PURE__*/React.createElement("img", _extends({}, imageProps(), {
    className: cs({
      [styles.blockImg]: !!block,
      [className]: !!className
    }),
    "data-testid": "not-lazy-image",
    ref: ref
  }))));
});
/* eslint-enable */

Image.propTypes = {
  /** Decorative images should use an empty alt value so they are ignored by screen readers. */
  alt: PropTypes.string,
  /** Whether to treat the image as block-level and fill its container. */
  block: PropTypes.bool,
  /** Optional classname to add to the image element. */
  className: PropTypes.string,
  /** Optional classname to add to the lazy loaded image wrapper. */
  containerClassName: PropTypes.string,
  /** Optional image file format: webp | jpeg | png-alpha */
  fileFormat: PropTypes.string,
  /** Whether to lazyload the image using intersection observer. */
  lazy: PropTypes.bool,
  /** Maximum width of the image asset - any slot sizes greater than this will be omitted from the srcset. */
  maxWidth: PropTypes.number,
  /** Optional prefix to add in the image URL, for searchability. */
  prefix: PropTypes.string,
  /** Optional flag that allows responsive images to preload by adding  preload resource to Next.js Head */
  preload: PropTypes.bool,
  /** Comma-separated list of media conditions and slot sizes for sizes attribute. Can be generated using the constructSizes() util. */
  sizes: PropTypes.string,
  /** Image URL to be used as the base to generate responsive images from. Also used as fallback image for older browsers. */
  src: PropTypes.string.isRequired,
  /** Image URL for mobile viewpoints */
  mobileSrc: PropTypes.string,
  /** HTML element tag to use for the image wrapper. */
  tag: PropTypes.string
};
Image.defaultProps = {
  alt: '',
  block: false,
  className: '',
  containerClassName: '',
  fileFormat: null,
  lazy: true,
  maxWidth: null,
  mobileSrc: null,
  prefix: '',
  preload: false,
  sizes: null,
  tag: 'span'
};
export default Image;